/**
 * ApprovalCard — modern card for a Teams Approvals item.
 *
 * Visual standard: mirrors RAO/components/InFlightCartCard (v0.1.51 cart cards).
 * No GUIDs, no raw IDs; customer-first content. Approve / Reject open the
 * decision modal; View detail flips into the request-detail expansion.
 */

import { useMemo } from 'react';
import { Button, Pill } from '@/components/primitives';
import { formatRelative } from '@/utils/time';
import type { GraphApproval } from './services/graphApprovalsClient';

export interface ApprovalCardProps {
  approval: GraphApproval;
  onApprove: (a: GraphApproval) => void;
  onReject: (a: GraphApproval) => void;
  onViewDetail: (a: GraphApproval) => void;
  /** When set, hides the action buttons (history view). */
  readOnly?: boolean;
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

function statusPill(s: GraphApproval['overallStatus']): { variant: 'ok' | 'warn' | 'err' | 'muted'; label: string } {
  switch (s) {
    case 'pending':
      return { variant: 'warn', label: 'Pending' };
    case 'approved':
      return { variant: 'ok', label: 'Approved' };
    case 'rejected':
      return { variant: 'err', label: 'Rejected' };
    case 'expired':
      return { variant: 'muted', label: 'Expired' };
    case 'canceled':
      return { variant: 'muted', label: 'Cancelled' };
    default:
      return { variant: 'muted', label: 'Unknown' };
  }
}

const KNOWN_KEYS_ORDER = ['amount', 'customer', 'branch', 'context', 'type', 'urgency'];

function orderedCustomProps(cp: Record<string, string>): Array<{ k: string; v: string }> {
  const seen = new Set<string>();
  const out: Array<{ k: string; v: string }> = [];
  for (const k of KNOWN_KEYS_ORDER) {
    if (cp[k]) {
      out.push({ k, v: cp[k]! });
      seen.add(k);
    }
  }
  for (const [k, v] of Object.entries(cp)) {
    if (!seen.has(k) && v) out.push({ k, v });
  }
  return out;
}

export function ApprovalCard({
  approval,
  onApprove,
  onReject,
  onViewDetail,
  readOnly,
}: ApprovalCardProps): JSX.Element {
  const status = statusPill(approval.overallStatus);
  const props = useMemo(() => orderedCustomProps(approval.customProperties), [approval.customProperties]);
  const requestedAgo = formatRelative(approval.createdDateTime);
  const dueIn = approval.dueDateTime ? formatRelative(approval.dueDateTime) : '';
  const decided = approval.stages.find((s) => s.reviewedDateTime);

  const cardClasses = ['rap-card', approval.urgent && approval.overallStatus === 'pending' ? 'rap-card--urgent' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <article className={cardClasses} aria-label={`Approval: ${approval.displayName}`}>
      {approval.urgent && approval.overallStatus === 'pending' && (
        <span className="rap-card__urgent-pill">
          <Pill variant="err">Urgent</Pill>
        </span>
      )}

      <div className="rap-card__identity">
        <div className="rap-card__avatar" aria-hidden>
          {initialsFromName(approval.requesterDisplayName)}
        </div>
        <div className="rap-card__identity-text">
          <div className="rap-card__title" title={approval.displayName}>
            {approval.displayName}
          </div>
          <div className="rap-card__requester">
            From {approval.requesterDisplayName}
          </div>
        </div>
      </div>

      <div className="rap-card__body">
        {props.length === 0 ? (
          <>
            <span className="rap-card__body-key">Source</span>
            <span className="rap-card__body-val">{approval.sourceApp}</span>
          </>
        ) : (
          props.map(({ k, v }) => (
            <span key={k} style={{ display: 'contents' }}>
              <span className="rap-card__body-key">{k}</span>
              <span className="rap-card__body-val">{v}</span>
            </span>
          ))
        )}
        {approval.dueDateTime && approval.overallStatus === 'pending' && (
          <>
            <span className="rap-card__body-key">Due</span>
            <span
              className="rap-card__body-val"
              style={
                approval.urgent
                  ? { color: 'var(--err-text)', fontWeight: 600 }
                  : undefined
              }
            >
              {dueIn}
            </span>
          </>
        )}
      </div>

      <div className="rap-card__actions">
        <Pill variant={status.variant}>{status.label}</Pill>
        <div className="rap-card__actions-spacer" />
        {!readOnly && approval.overallStatus === 'pending' && approval.activeStageId && (
          <>
            <Button variant="ghost" size="sm" onClick={() => onViewDetail(approval)}>
              View detail
            </Button>
            <Button variant="danger" size="sm" onClick={() => onReject(approval)}>
              Reject
            </Button>
            <Button variant="green" size="sm" onClick={() => onApprove(approval)}>
              Approve
            </Button>
          </>
        )}
        {(readOnly || approval.overallStatus !== 'pending') && (
          <Button variant="ghost" size="sm" onClick={() => onViewDetail(approval)}>
            View detail
          </Button>
        )}
      </div>

      <div className="rap-card__footer">
        <span>Requested {requestedAgo}</span>
        <span className="rap-card__footer-sep" aria-hidden>·</span>
        <span>by {approval.requesterDisplayName}</span>
        <span className="rap-card__footer-sep" aria-hidden>·</span>
        <span>{approval.sourceApp}</span>
        {decided?.reviewedDateTime && (
          <>
            <span className="rap-card__footer-sep" aria-hidden>·</span>
            <span>Decided {formatRelative(decided.reviewedDateTime)}</span>
          </>
        )}
      </div>
    </article>
  );
}

export function ApprovalCardSkeleton(): JSX.Element {
  return (
    <div className="rap-card rap-card--skeleton" aria-hidden>
      <div className="rap-card__identity">
        <div className="rap-card__avatar rap-skel" style={{ borderRadius: '50%' }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div className="rap-skel" style={{ height: 14, width: '70%' }} />
          <div className="rap-skel" style={{ height: 10, width: '40%' }} />
        </div>
      </div>
      <div className="rap-skel" style={{ height: 60, width: '100%' }} />
      <div className="rap-skel" style={{ height: 24, width: '90%' }} />
    </div>
  );
}
